import {
    ConversationItem,
    ConversationSpec,
    Block,
    TYPE_REVIEW_LINK_BUTTON,
} from '@/entities/conversation'
import { metrics } from '@/domain/NPS'

export type Cursor = Array<string | number>

export interface Scope {
    [key: string]: boolean | string[] | { key: string; value: string }[] // option keys if a ReceiveOption
}

export interface OptionScope {
    [key: string]: string[]
}

export type SpecNode = ConversationItem | Block

export interface Visited {
    [label: string]: boolean
}

export interface NodePath {
    node: SpecNode
    cursor: Cursor
    scope: Scope
}

export function getNode(
    spec: ConversationSpec,
    cursor: Cursor
): ConversationItem | Block | undefined {
    const keys = [...cursor] // need to copy cursor so we don't mutate it
    let ref: ConversationItem | ConversationSpec = spec

    while (keys.length > 0) {
        const key = keys.shift()
        if (ref !== undefined && key !== undefined) {
            ref = ref[key]
        }
    }

    return ref as ConversationItem
}

export function initialCursor(spec: ConversationSpec): Cursor {
    return findBlockCursor(spec, 'start') || [0]
}

export function finishCursor(spec: ConversationSpec): Cursor | undefined {
    return findBlockCursor(spec, 'finish') || undefined
}

function isLeavingFinishBlock(
    finishedCursor: Cursor | undefined,
    currentCursor: Cursor,
    nextCursor: Cursor
): boolean {
    // Check if all cursors are valid arrays with at least one element
    const cursorsValid = [finishedCursor, currentCursor, nextCursor].every(
        (cursor) => cursor && Array.isArray(cursor) && cursor.length > 0
    )

    return (
        cursorsValid &&
        finishedCursor?.[0] === currentCursor?.[0] && // if we are currently in the finish block
        finishedCursor?.[0] !== nextCursor?.[0] // and the next node is outside of the finish block
    )
}

export function findBlockCursor(
    spec: ConversationSpec,
    label: string
): Cursor | undefined {
    for (let i = 0; i < spec.length; i++) {
        const item = spec[i]
        if (item.type === 'Block' && item.label === label) {
            return [i, 'items', 0] // reset to top level
        }
    }
}

// Get the top-level block identifier for a given cursor, if available
function getBlockLabelByCursor(
    spec: ConversationSpec,
    cursor: Cursor
): string | undefined {
    const topIndex = cursor?.[0]
    if (typeof topIndex === 'number') {
        const block = spec[topIndex]
        if (block && block.type === 'Block') {
            return `block_${topIndex}_${block.label}`
        }
    }
    return undefined
}

// get the latest numeric key in the cursor and try to increment it
// if there is a node there, return the cursor to it
// this walks down lists of items, and returns control to the closest
// parent if the list is finished
export function getNextCursorDownstream(
    spec: ConversationSpec,
    cursor: Cursor,
    node: SpecNode
): Cursor | undefined {
    for (let i = cursor.length - 1; i >= 0; i--) {
        // start at the end and go <-

        const key = cursor[i]

        if (typeof key === 'number') {
            const nextCursor = cursor.slice(0, i).concat([key + 1])
            const nextNode = getNode(spec, nextCursor)

            if (nextNode && nextNode !== node) {
                return nextCursor
            }
        }
    }
}

export function getPrevCursor(
    spec: ConversationSpec,
    cursor: Cursor,
    node: SpecNode
): Cursor | undefined {
    for (let i = cursor.length - 1; i >= 0; i--) {
        // start at the end and go <-
        const key = cursor[i]
        if (typeof key === 'number' && key > 0) {
            const prevCursor = cursor.slice(0, i).concat([key - 1])
            const prevNode = getNode(spec, prevCursor)
            if (prevNode && prevNode !== node) {
                return prevCursor
            }
        }
    }
}

export function countMaxReachableInputs(
    spec: ConversationSpec,
    cursor: Cursor
): number {
    const bestDepth: Map<string, number> = new Map()
    // Path-scoped guard: carry the set of visited block labels with each queued path
    const initialVisitedBlocks = new Set<string>()
    const initialBlockLabel = getBlockLabelByCursor(spec, cursor)
    const finishedCursor = finishCursor(spec)

    if (initialBlockLabel) initialVisitedBlocks.add(initialBlockLabel)
    const queue: {
        cursor: Cursor
        depth: number
        visitedBlocks: Set<string>
    }[] = [{ cursor, depth: 0, visitedBlocks: initialVisitedBlocks }]
    let maxInputs = 0

    while (queue.length > 0) {
        const { cursor: currentCursor, depth, visitedBlocks } = queue.shift()!

        // Create a unique key for this position to avoid infinite loops
        const positionKey = JSON.stringify(currentCursor)
        const prevBest = bestDepth.get(positionKey)
        if (prevBest !== undefined && prevBest >= depth) {
            continue
        }
        bestDepth.set(positionKey, depth)

        const node = getNode(spec, currentCursor)
        if (!node) {
            continue
        }

        let nextCursors: Cursor[] = []

        switch (node.type) {
            case 'ReceiveText':
            case 'ReceiveOption':
            case 'Say':
                nextCursors = [
                    getNextCursorDownstream(spec, currentCursor, node),
                ].filter(Boolean) as Cursor[]
                break

            case 'For':
                // Explore all possible branches
                {
                    let hasMissingCases = false
                    if (node?.match === 'rating') {
                        const options = Object.keys(node.cases)
                        // default means it never falls through, so we say it has no missing cases
                        if (!options.includes('default')) {
                            for (const metric of metrics) {
                                // Check which metric we are dealing with
                                const intersection = metric.filter((item) =>
                                    options.includes(item)
                                )

                                if (intersection.length > 0) {
                                    // If options array is missing any elements then it needs to fall through
                                    const missing = metric.filter(
                                        (item) => !options.includes(item)
                                    )
                                    if (missing.length > 0) {
                                        hasMissingCases = true
                                    }

                                    // there will only be one metric matching the options
                                    break
                                }
                            }
                        }
                    } else {
                        let count = -1
                        //point cursor at previous list
                        const prevCursor = getPrevCursor(
                            spec,
                            currentCursor,
                            node
                        )
                        if (prevCursor) {
                            const prevNode = getNode(spec, prevCursor)
                            if (prevNode && 'options' in prevNode) {
                                // If the previous list has options, use its length
                                count = prevNode.options?.length ?? -1
                            }
                        }
                        // if the previous list has more options than there are cases then it needs to fall through
                        if (Object.keys(node.cases).length < count) {
                            hasMissingCases = true
                        }
                    }
                    let hasNonEmptyCases = false
                    Object.keys(node.cases).forEach((caseKey) => {
                        if (node.cases[caseKey]?.length > 0) {
                            hasNonEmptyCases = true
                            nextCursors.push([
                                ...currentCursor,
                                'cases',
                                caseKey,
                                0,
                            ])
                        }
                    })

                    // If all cases are empty, fall through to next sibling
                    if (!hasNonEmptyCases || hasMissingCases) {
                        const fallbackCursor = getNextCursorDownstream(
                            spec,
                            currentCursor,
                            node
                        )
                        if (fallbackCursor) {
                            if (!hasNonEmptyCases) {
                                nextCursors = [fallbackCursor]
                            } else {
                                // If only some cases are empty, add a fall through option to list
                                nextCursors.push(fallbackCursor)
                            }
                        }
                    }
                }
                break

            case 'Jump':
                {
                    const jumpLabel = node.label
                    const jumpCursor = findBlockCursor(spec, jumpLabel)
                    if (jumpCursor) {
                        // Get the label identifier for the target block
                        const targetBlockId = getBlockLabelByCursor(
                            spec,
                            jumpCursor
                        )
                        // Path-scoped guard: avoid revisiting the same block within this path
                        if (
                            targetBlockId &&
                            !visitedBlocks.has(targetBlockId)
                        ) {
                            nextCursors = [jumpCursor]
                        }
                    }
                }
                break

            case 'Block':
                if (node.items?.length > 0) {
                    nextCursors = [[...currentCursor, 'items', 0]]
                } else {
                    const fallback = getNextCursorDownstream(
                        spec,
                        currentCursor,
                        node
                    )
                    if (fallback) nextCursors = [fallback]
                }
                break
        }

        // Add next positions to queue and count inputs in those paths
        nextCursors.forEach((nextCursor) => {
            const nextNode = getNode(spec, nextCursor)
            const nextNodeType = nextNode?.type
            let inputCount = 0

            // Skip if we're in the finish block and the next node is outside the finish block
            if (
                isLeavingFinishBlock(finishedCursor, currentCursor, nextCursor)
            ) {
                return
            }

            // Count input nodes
            if (
                nextNode &&
                (nextNodeType === 'ReceiveText' ||
                    nextNodeType === 'ReceiveOption') &&
                nextNode.param != TYPE_REVIEW_LINK_BUTTON
            ) {
                inputCount = 1
            }

            // Count AI questions from Say nodes
            if (
                nextNode &&
                nextNodeType === 'Say' &&
                nextNode.aiQuestionCount
            ) {
                // We subtract 1 because for AI questions the node itself is not an input
                inputCount = nextNode.aiQuestionCount - 1
            }

            const nextDepth = depth + inputCount
            maxInputs = Math.max(maxInputs, nextDepth)

            // Update path-scoped visited blocks for the next state
            const nextVisitedBlocks = new Set(visitedBlocks)
            const nextBlockLabel = getBlockLabelByCursor(spec, nextCursor)
            if (nextBlockLabel) nextVisitedBlocks.add(nextBlockLabel)

            queue.push({
                cursor: nextCursor,
                depth: nextDepth,
                visitedBlocks: nextVisitedBlocks,
            })
        })
    }

    return maxInputs
}
